import { motion } from "framer-motion";

const PILLARS = [
  {
    n: "01",
    title: "Discreet retained search",
    body: "Sensitive mandates handled with absolute confidentiality — referenced through trust, not databases.",
  },
  {
    n: "02",
    title: "Relationship-led hiring",
    body: "Long-form relationships with senior operators across the UK technology, commercial and operational leadership market.",
  },
  {
    n: "03",
    title: "Quality over volume",
    body: "We run a small number of mandates concurrently. Every brief receives founder-grade attention end-to-end.",
  },
  {
    n: "04",
    title: "Built for founders, scaling",
    body: "From first commercial hire to board appointment — calibrated to the stage, capital structure and pace of your business.",
  },
];

const TEXTURE =
  "https://static.prod-images.emergentagent.com/jobs/4e1ae213-eba7-4c3b-9a90-84915d76d4df/images/1d80d3d4736a5e2aca1870e1eaea854c7d0d272080b264db24e0435dd6bf1e96.png";

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative overflow-hidden bg-[#15315F] py-24 md:py-36"
    >
      <div className="absolute inset-0 opacity-25 mix-blend-screen pointer-events-none">
        <img src={TEXTURE} alt="" className="h-full w-full object-cover" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#15315F] via-[#15315F]/85 to-[#0E2750]" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <motion.span
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-mono-eyebrow text-[#C9A876]"
            >
              ◆ Why Arcpoint
            </motion.span>
            <motion.h2
              data-testid="about-heading"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-5 font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-white"
            >
              Helping founders scale through{" "}
              <span className="font-serif-accent text-[#C9A876]">deliberate</span>{" "}
              leadership hires.
            </motion.h2>
            <p className="mt-8 text-slate-300 leading-relaxed text-base md:text-lg">
              Arcpoint is a founder-led executive search firm. We partner with
              founders, MDs and investors to secure the senior operators who
              shape the next phase of growth — quietly, commercially, and with
              deep market intelligence.
            </p>
            <div className="hairline mt-10" />
            <blockquote className="mt-10">
              <p className="font-serif-accent text-2xl md:text-3xl text-white leading-snug">
                “Great companies are built on the operators few people know,
                and even fewer can reach.”
              </p>
              <footer className="mt-6 font-mono-eyebrow text-slate-400">
                — Founder, Arcpoint
              </footer>
            </blockquote>
          </div>

          <div className="lg:col-span-7">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/5 border border-white/5">
              {PILLARS.map((p, i) => (
                <motion.li
                  key={p.title}
                  data-testid={`about-pillar-${i}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="bg-[#15315F] p-8 md:p-10 hover:bg-white/[0.03] transition-colors duration-500 group"
                >
                  <div className="flex items-baseline justify-between mb-6">
                    <span className="font-mono-eyebrow text-[#C9A876]">{p.n}</span>
                    <div className="h-px flex-1 ml-4 bg-gradient-to-r from-[#C9A876]/30 to-transparent" />
                  </div>
                  <h3 className="font-display text-xl md:text-2xl text-white tracking-tight mb-3">
                    {p.title}
                  </h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{p.body}</p>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
